/**
 * cesium_glb_highlight.js
 *
 * Highlights entity_ids from action-mode Archie results in a normal
 * GLB/Cesium scene (NOT iTwin).
 *
 * Public API (registered on window by registerCesiumGlbHighlight())
 * ─────────────────────────────────────────────────────────────────
 *   window.highlightCesiumGlbEntities(entityIds, entityTooltipMap)
 *     entityIds        – string[]  IDs extracted from layer/prim attributes
 *     entityTooltipMap – { [id]: { _label, ...sublayerAttributes } }
 *
 *   window._clearCesiumGlbHighlight()
 *     Restores all original colours, destroys the hover handler, hides tooltip.
 *     Called automatically on the next highlightCesiumGlbEntities() call and
 *     on component unmount.
 *
 * Strategy
 * ────────
 *   1. Try to find each entity_id as a Cesium Entity in viewer.entities
 *      (individual building elements loaded as separate entities).
 *   2. Matches found  → highlight orange (#ff6a00), fade everything else (α 0.08).
 *   3. No match found → tint the whole window.drone model orange (α 0.6) so the
 *      user knows the result relates to the loaded building.
 *   4. ScreenSpaceEventHandler on MOUSE_MOVE shows a tooltip with the sublayer
 *      attribute data for each entity when hovered.
 */

const TOOLTIP_ID        = 'cesium-glb-entity-tooltip';
const HIGHLIGHT_COLOR   = '#ff6a00';  // Orange for KPI heatmap
const ASSET_QUERY_COLOR = '#00bfff';  // Sky blue for asset queries (Deep Sky Blue)
const FADE_ALPHA        = 0.15;   // Increased from 0.08 to 0.15 for better visibility of non-highlighted entities
const HIGHLIGHT_ALPHA   = 1.0;
const WHOLE_MODEL_ALPHA = 1.0;    // keep model at full opacity; use silhouette for the highlight

// ── Colour helpers ────────────────────────────────────────────────────────────

const applyModelColor = (entity, cssColor, alpha, originalStates) => {
    if (entity.model) {
        originalStates.set(entity, {
            color:            entity.model.color,
            colorBlendMode:   entity.model.colorBlendMode,
            colorBlendAmount: entity.model.colorBlendAmount,
            silhouetteColor:  entity.model.silhouetteColor,
            silhouetteSize:   entity.model.silhouetteSize,
        });
        // Restore original texture — only silhouette used for whole-model case.
        // For individual entities (matched by ID) we also add a warm HIGHLIGHT tint.
        if (alpha < 1.0) {
            // Fading unrelated entities — semi-transparent grey
            entity.model.color            = Cesium.Color.fromCssColorString('#4a5568').withAlpha(alpha);
            entity.model.colorBlendMode   = Cesium.ColorBlendMode.MIX;
            entity.model.colorBlendAmount = 0.65;
        } else {
            // Highlighting — REPLACE mode forces the solid highlight colour onto
            // every fragment regardless of the original PBR material/texture,
            // so the entire surface is filled (not just the silhouette outline).
            entity.model.color            = Cesium.Color.fromCssColorString(cssColor).withAlpha(1.0);
            entity.model.colorBlendMode   = Cesium.ColorBlendMode.REPLACE;
            entity.model.colorBlendAmount = 1.0;
        }
        // Silhouette outline — crisp border on top of the filled surface
        entity.model.silhouetteColor = Cesium.Color.fromCssColorString(cssColor).withAlpha(1.0);
        entity.model.silhouetteSize  = 3.0;
        console.log('✅ [cesium_glb_highlight] applyModelColor → model entity, color set to', cssColor, 'alpha', alpha);
    } else if (entity.billboard) {
        originalStates.set(entity, { color: entity.billboard.color });
        entity.billboard.color = new Cesium.ConstantProperty(color);
        console.log('✅ [cesium_glb_highlight] applyModelColor → billboard entity');
    } else if (entity.point) {
        originalStates.set(entity, { color: entity.point.color });
        entity.point.color = new Cesium.ConstantProperty(color);
        console.log('✅ [cesium_glb_highlight] applyModelColor → point entity');
    } else {
        console.warn('⚠️ [cesium_glb_highlight] applyModelColor — entity has no model/billboard/point:', entity);
    }
    // Force Cesium to re-render the scene (in case the viewer uses passive rendering)
    window.viewer?.scene?.requestRender?.();
};

const restoreAll = (originalStates) => {
    originalStates.forEach((orig, entity) => {
        if (entity.model) {
            // Cesium Entity with ModelGraphics
            entity.model.color            = orig.color;
            entity.model.colorBlendMode   = orig.colorBlendMode;
            entity.model.colorBlendAmount = orig.colorBlendAmount;
            entity.model.silhouetteColor  = orig.silhouetteColor;
            entity.model.silhouetteSize   = orig.silhouetteSize;
        } else if (orig.colorBlendMode !== undefined) {
            // Raw Cesium Model primitive
            entity.color            = orig.color;
            entity.colorBlendMode   = orig.colorBlendMode;
            entity.colorBlendAmount = orig.colorBlendAmount;
        } else if (entity.billboard && orig.color) {
            entity.billboard.color = orig.color;
        } else if (entity.point && orig.color) {
            entity.point.color = orig.color;
        }
    });
    originalStates.clear();
    window.viewer?.scene?.requestRender?.();
};

// ── Tooltip helpers ───────────────────────────────────────────────────────────

const getOrCreateTooltip = () => {
    let tooltip = document.getElementById(TOOLTIP_ID);
    if (!tooltip) {
        tooltip = document.createElement('div');
        tooltip.id = TOOLTIP_ID;
        tooltip.style.cssText = [
            'position:fixed',
            'z-index:2147483646',
            'background:rgba(22,33,62,0.97)',
            'color:#fff',
            'padding:10px 16px',
            'border-radius:8px',
            'font-size:12px',
            'font-family:"Suisse Intl",sans-serif',
            'pointer-events:none',
            'display:none',
            'border:1px solid rgba(0,191,255,0.65)',  // Sky blue border for asset queries
            'box-shadow:0 4px 16px rgba(0,0,0,0.55)',
            'width:max-content',
            'min-width:160px',
            'max-width:420px',
            'max-height:60vh',
            'overflow-y:auto',
            'overflow-x:hidden',
            'line-height:1.65',
            'word-break:break-word',
            'white-space:normal',
        ].join(';');
        document.body.appendChild(tooltip);
    }
    return tooltip;
};

// Keys that are never shown in the tooltip (internal / metadata)
const TOOLTIP_SKIP_KEYS = new Set([
    'entity_ids', 'element_ids', 'annotation', 'name', 'type',
    'prim_id', 'scene_id', 'stage_id', 'layer_id', 'sublayer_id', 'id', 'version',
]);

const formatTooltipHtml = (data, elementName = '') => {
    if (!data || Object.keys(data).length === 0) return '';

    const highlightColor = ASSET_QUERY_COLOR;

    // ── 1. Element name header ─────────────────────────────────────────────
    const nameRow = elementName
        ? `<div style="font-size:11px;color:#94a3b8;margin-bottom:6px;padding-bottom:4px;border-bottom:1px solid rgba(255,255,255,0.12)">🔩 Element: <strong style="color:#e2e8f0">${elementName}</strong></div>`
        : '';

    // ── 2. Component label ─────────────────────────────────────────────────
    const label     = data['_label'] || '';
    const floor     = data['_floor'] || '';
    const floorUnit = data['_floor_unit'] || 'kWh';
    const floorTotal = data['_floor_total'];
    const defaultUnit = data['unit'] || '';

    const labelRow = label
        ? `<div style="font-weight:700;color:${highlightColor};margin-bottom:6px;font-size:13px">${label}</div>`
        : '';

    // ── 3. Unit helper ─────────────────────────────────────────────────────
    // For cost keys, use the actual currency stored in the data object
    // (e.g. 'EUR') rather than hard-coding '$'.
    const currency = data['Currency'] || data['currency'] || '';
    const inferUnit = (key) => {
        const k = key.toLowerCase();
        if (k.includes('cost') || k.includes('rate') || k.includes('price')) return currency || '$';
        if (k.includes('carbon') || k.includes('kgco2') || k.includes('co2')) return 'kgCO2e';
        if (k.includes('demand') || k.includes('energy') || k.includes('consumption')) return 'kWh';
        if (k.includes('temperature')) return '°C';
        if (k.includes('radiation') || k.includes('irradiance')) return 'W/m²';
        if (k.includes('area') || k.includes('surface')) return 'm²';
        if (k.includes('volume')) return 'm³';
        if (k.includes('percent') || k.includes('pmv') || k.includes('ppd')) return '';
        if (k.includes('duration') || k.includes('hours')) return 'hrs';
        return defaultUnit;
    };

    // ── 4. Reserved / skip sets ────────────────────────────────────────────
    const RESERVED = new Set(['_label','_floor','_floor_total','_floor_unit','unit','annotation','name','type',
        'entity_ids','element_ids','prim_id','scene_id','stage_id','layer_id','sublayer_id','id','version',
        'Currency','currency'  // shown implicitly via inferUnit; don't render as a raw row
    ]);

    const DEMAND_DISPLAY_NAMES = {
        heating_demand:         'Heating Demand',
        cooling_demand:         'Cooling Demand',
        lighting_demand:        'Lighting Demand',
        equipment_demand:       'Equipment Demand',
        total_demand:           'Total Demand',
        floor_heating_demand:   'Heating',
        floor_cooling_demand:   'Cooling',
        floor_lighting_demand:  'Lighting',
        floor_equipment_demand: 'Equipment',
    };

    const ENTITY_ORDER = ['heating_demand','cooling_demand','lighting_demand','equipment_demand','total_demand'];
    const FLOOR_ORDER  = ['floor_heating_demand','floor_cooling_demand','floor_lighting_demand','floor_equipment_demand'];

    const makeRow = (k, v, bold = false, topBorder = false) => {
        const displayKey = DEMAND_DISPLAY_NAMES[k]
            ?? k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const num = parseFloat(String(v).replace(/,/g, ''));
        const val = !isNaN(num) ? num.toLocaleString('en-US', { maximumFractionDigits: 0 }) : v;
        const unit = inferUnit(k);
        const border = topBorder ? ';border-top:1px solid rgba(255,255,255,0.12);padding-top:5px;margin-top:5px' : '';
        return `<div style="margin-bottom:3px${border}">` +
            `<span style="color:#94a3b8;margin-right:4px">${displayKey}:</span>` +
            `<strong${bold ? ` style="color:${highlightColor}"` : ''}>${val}${unit ? ` ${unit}` : ''}</strong></div>`;
    };

    // ── 5. Entity-level rows (non floor_ keys) ─────────────────────────────
    const entityEntries = Object.entries(data)
        .filter(([k, v]) => {
            if (RESERVED.has(k) || TOOLTIP_SKIP_KEYS.has(k)) return false;
            if (k.startsWith('floor_')) return false;
            return !isNaN(parseFloat(String(v).replace(/,/g, ''))) && String(v).trim() !== '';
        })
        .sort(([a], [b]) => {
            const ai = ENTITY_ORDER.indexOf(a), bi = ENTITY_ORDER.indexOf(b);
            if (ai !== -1 && bi !== -1) return ai - bi;
            if (ai !== -1) return -1;
            if (bi !== -1) return 1;
            return 0;
        });

    const entityRows = entityEntries
        .map(([k, v], i) => makeRow(k, v, k === 'total_demand', k === 'total_demand' && i > 0))
        .join('');

    // ── 6. Floor breakdown section (floor_ prefixed keys) ─────────────────
    const floorEntries = Object.entries(data)
        .filter(([k, v]) => {
            if (!k.startsWith('floor_')) return false;
            return !isNaN(parseFloat(String(v).replace(/,/g, ''))) && String(v).trim() !== '';
        })
        .sort(([a], [b]) => {
            const ai = FLOOR_ORDER.indexOf(a), bi = FLOOR_ORDER.indexOf(b);
            if (ai !== -1 && bi !== -1) return ai - bi;
            if (ai !== -1) return -1;
            if (bi !== -1) return 1;
            return 0;
        });

    let floorSection = '';
    if (floorEntries.length > 0 || floorTotal != null) {
        const hdr = `font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:0.05em;`
                  + `margin-top:8px;margin-bottom:4px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.10)`;
        floorSection = `<div style="${hdr}">🏢 ${floor ? `${floor} —` : ''} Floor breakdown</div>`;
        floorSection += floorEntries.map(([k, v]) => makeRow(k, v, false, false)).join('');
        if (floorTotal != null) {
            floorSection += `<div style="margin-top:4px;border-top:1px solid rgba(255,255,255,0.08);padding-top:4px">` +
                `<span style="color:#94a3b8;margin-right:4px">Floor Total:</span>` +
                `<strong>${Number(floorTotal).toLocaleString('en-US',{maximumFractionDigits:0})} ${floorUnit}</strong></div>`;
        }
    }

    const body = entityRows || '<div style="color:#94a3b8;font-size:11px">No data</div>';
    return nameRow + labelRow + body + floorSection;
};

// ── KPI attribute priority per response type ──────────────────────────────────
// Defines which numeric attribute to use for heatmap colour intensity.
// Keys match values in tags.data_characteristics from arche-engine.
const KPI_ATTR_PRIORITY = {
    'cost':            ['Total Cost', 'Net Cost', 'net_cost', 'total_cost', 'Material Cost', 'Labor Cost', 'Rate(per unit)', 'rate'],
    'Comfort':         ['PMV', 'PPD', 'operative_temperature', 'thermal_comfort_index', 'comfort_index'],
    'Energy Demand':   ['total_demand', 'heating_demand', 'cooling_demand', 'lighting_demand', 'equipment_demand'],
    'Solar Radiation': ['radiation_rate_avg', 'annual_radiation_total', 'direct_irradiance', 'solar_heat_gain', 'diffuse_irradiance'],
    'shadow':          ['shadow_duration_percent', 'radiation_rate_avg'],
    'embodied_carbon': ['embodied_carbon', 'total_embodied_carbon', 'carbon_value', 'kgco2e'],
};

const SKIP_KPI_KEYS = new Set(['_label', 'entity_ids', 'element_ids', 'unit', 'annotation', 'name', 'type']);

/**
 * Find the primary numeric KPI value from a tooltip data object.
 * Uses the attribute priority list for the active KPI type, then falls back
 * to the first numeric attribute found.
 */
const getPrimaryKpiValue = (tooltipData, dataChars = []) => {
    if (!tooltipData) return null;
    // Try priority attributes for the matched KPI type first
    for (const [key, attrs] of Object.entries(KPI_ATTR_PRIORITY)) {
        if (!dataChars.some(c => c.toLowerCase().includes(key.toLowerCase()))) continue;
        for (const attr of attrs) {
            const v = tooltipData[attr];
            if (v !== undefined) {
                const n = parseFloat(v);
                if (!isNaN(n)) return n;
            }
        }
    }
    // Generic fallback: first numeric attribute (skip metadata keys)
    for (const [k, v] of Object.entries(tooltipData)) {
        if (SKIP_KPI_KEYS.has(k)) continue;
        const n = parseFloat(v);
        if (!isNaN(n)) return n;
    }
    return null;
};

// ── Core highlight function ───────────────────────────────────────────────────

const highlightCesiumGlbEntities = (entityIds, entityTooltipMap, tags = {}) => {
    if (!window.viewer) { console.error('❌ [highlight] window.viewer is not set'); return; }

    // Always clean up the previous state first
    if (typeof window._clearCesiumGlbHighlight === 'function') {
        window._clearCesiumGlbHighlight();
    }

    // ── 🔍 DEBUG: dump incoming args ──────────────────────────────────────────
    console.group('🔍 [highlight] highlightCesiumGlbEntities called');
    console.log('  entityIds (first 10):', entityIds.slice(0, 10));
    console.log('  total entityIds:', entityIds.length);
    console.log('  tags:', tags);
    console.log('  entityTooltipMap keys (first 10):', Object.keys(entityTooltipMap).slice(0, 10));
    console.log('  window.glbNodeNames present?', !!window.glbNodeNames, 'length:', window.glbNodeNames?.length);
    console.log('  window.drone present?', !!window.drone);
    console.log('  window.drone.model present?', !!window.drone?.model);
    console.log('  window.viewer.entities.values.length:', window.viewer?.entities?.values?.length);
    console.groupEnd();

    const entityIdSet    = new Set(entityIds.map(String));
    const originalStates = new Map();

    // ── 1. Partition viewer.entities into matched / unmatched ─────────────────
    const matched   = [];
    const unmatched = [];

    window.viewer.entities.values.forEach(entity => {
        const eid  = String(entity.id   || '');
        const name = String(entity.name || '');
        if (entityIdSet.has(eid) || entityIdSet.has(name)) {
            matched.push({ entity, key: entityIdSet.has(eid) ? eid : name });
        } else {
            unmatched.push(entity);
        }
    });

    if (matched.length > 0) {
        // Highlight matched entities; fade all others
        console.log('🎯 [highlight] PATH: matched Cesium entities by ID, count =', matched.length);
        console.log('🎯 [highlight] matched entity IDs:', matched.map(m => m.key).slice(0, 10));
        console.log('🎯 [highlight] first matched entity type:', matched[0]?.entity?.model ? 'model' : matched[0]?.entity?.billboard ? 'billboard' : 'other');
        matched.forEach(({ entity }) =>
            applyModelColor(entity, HIGHLIGHT_COLOR, HIGHLIGHT_ALPHA, originalStates)
        );
        unmatched.forEach(entity =>
            applyModelColor(entity, '#ffffff', FADE_ALPHA, originalStates)
        );
    } else {
        // No individual Cesium entity found.
        // Attempt per-element CustomShader highlight using the _FEATURE_ID_0
        // attribute and window.glbNodeNames injected by glbFlattener.

        console.log('🔍 [highlight] PATH: no entity ID match → trying CustomShader/glbNodeNames path');
        console.log('  window.glbNodeNames?.length =', window.glbNodeNames?.length);
        console.log('  window.drone?.model =', !!window.drone?.model);

        if (!window.glbNodeNames?.length || !window.drone?.model) {
            console.warn('⚠️ [highlight] glbNodeNames or drone model missing — nothing highlighted.');
            return;
        }

        // Map element name → ALL node indices that carry that name.
        // Revit/Rhino GLBs split a single BIM element across multiple nodes
        // (e.g. top-half and bottom-half of a cylindrical duct are two separate
        // nodes both named "3030").  A plain Map keeps only the LAST index, so
        // half the geometry stays ghost-grey and looks like a highlighted line.
        const nameToIndices = new Map();   // name → number[]
        window.glbNodeNames.forEach((name, idx) => {
            if (!name) return;
            const arr = nameToIndices.get(name);
            if (arr) arr.push(idx);
            else nameToIndices.set(name, [idx]);
        });

        // Helper: get ALL node indices for an entity ID (safe – always returns array)
        const indicesFor = (id) => nameToIndices.get(String(id)) ?? [];

        console.log('🔍 [highlight] nameToIdx size (non-empty node names):', nameToIndices.size);
        console.log('🔍 [highlight] sample glbNodeNames (first 5):', window.glbNodeNames.slice(0, 5));
        // Log any names that map to >1 node (the split-element case)
        const multiNodeNames = [...nameToIndices.entries()].filter(([,v]) => v.length > 1);
        if (multiNodeNames.length > 0) {
            console.log(`🔍 [highlight] ${multiNodeNames.length} element names map to multiple nodes (e.g. "${multiNodeNames[0][0]}" → nodes [${multiNodeNames[0][1].join(', ')}])`);
        }

        const highlightIndices = [];
        const notFoundIds = [];
        entityIds.forEach(id => {
            const idxs = indicesFor(id);
            if (idxs.length) highlightIndices.push(...idxs);
            else notFoundIds.push(String(id));
        });

        console.log(`🔍 [highlight] entityIds resolved: ${entityIds.length - notFoundIds.length} IDs found → ${highlightIndices.length} node indices, ${notFoundIds.length} NOT found in glbNodeNames`);
        if (notFoundIds.length > 0) {
            console.warn('⚠️ [highlight] IDs not found in glbNodeNames (first 10):', notFoundIds.slice(0, 10));
            const sampleId = notFoundIds[0];
            const partialMatches = window.glbNodeNames.filter(n => n && (n.includes(sampleId) || sampleId.includes(n)));
            console.warn(`⚠️ [highlight] Partial matches in glbNodeNames for "${sampleId}":`, partialMatches.slice(0, 5));
        }

        if (highlightIndices.length === 0) {
            console.error('❌ [highlight] No entity IDs matched glbNodeNames — nothing highlighted.');
            return;
        }

        console.log(`🎨 [highlight] CustomShader: highlighting ${highlightIndices.length} node indices (from ${entityIds.length - notFoundIds.length} entity IDs) of ${window.glbNodeNames.length} total nodes`);

        // ── Detect query type ─────────────────────────────────────────────
        const dataChars = tags?.data_characteristics || [];
        const isAssetQuery = dataChars.some(c =>
            c.toLowerCase().includes('fetch') ||
            c.toLowerCase().includes('asset query')
        );
        const isComplianceFail = dataChars.includes('compliance_fail');
        console.log(`📊 [cesium_glb_highlight] Query type: ${isComplianceFail ? 'Compliance FAIL (red)' : isAssetQuery ? 'Asset Query (solid blue)' : 'KPI-based (heatmap)'}, tags:`, dataChars);

        // ── Extract primary numeric KPI value per node (only for heatmaps) ────
        let nodeRawValues = new Map();   // nodeIdx → raw numeric value
        let minVal = 0, maxVal = 1, valRange = 1;
        
        if (!isAssetQuery && !isComplianceFail) {
            // KPI-based query: collect numeric values for heatmap
            // Each entity ID may map to multiple nodes — assign the same KPI value
            // to every node index so the whole element is coloured uniformly.
            entityIds.forEach(id => {
                const val = getPrimaryKpiValue(entityTooltipMap[String(id)], dataChars);
                if (val !== null) {
                    indicesFor(id).forEach(idx => nodeRawValues.set(idx, val));
                }
            });

            const allVals = [...nodeRawValues.values()];
            minVal   = allVals.length ? Math.min(...allVals) : 0;
            maxVal   = allVals.length ? Math.max(...allVals) : 1;
            valRange = (maxVal - minVal) || 1;
            console.log(`📊 [cesium_glb_highlight] KPI range: ${minVal.toFixed(2)} – ${maxVal.toFixed(2)}`);

            const idVariance = (idStr) => {
                let h = 5381;
                for (let i = 0; i < idStr.length; i++) h = ((h << 5) + h) ^ idStr.charCodeAt(i);
                return ((h >>> 0) % 1000) / 1000;
            };
            const VARIANCE_SCALE = 0.10;

            const enhancedValues = new Map();
            entityIds.forEach(id => {
                const idxs = indicesFor(id);
                if (!idxs.length) return;
                const raw      = nodeRawValues.get(idxs[0]);
                const baseNorm = raw !== undefined ? (raw - minVal) / valRange : 0.5;
                const variance = (idVariance(String(id)) - 0.5) * VARIANCE_SCALE;
                const finalNorm = Math.max(0.0, Math.min(1.0, baseNorm + variance));
                // Apply the same finalNorm to ALL nodes of this element
                idxs.forEach(idx => enhancedValues.set(idx, finalNorm));
            });
            nodeRawValues = enhancedValues;
        }

        // ── Build 1×N RGBA texture ────────────────────────────────────────────
        const nodeCount = window.glbNodeNames.length;
        const texData   = new Uint8Array(nodeCount * 4);
        let texHighlightedCount = 0;
        entityIds.forEach(id => {
            // Highlight ALL node indices for this entity ID
            indicesFor(id).forEach(idx => {
                if (idx >= nodeCount) return;
                if (isComplianceFail) {
                    texData[idx * 4 + 0] = 254;
                } else if (isAssetQuery) {
                    texData[idx * 4 + 0] = 255;
                } else {
                    const finalNorm = nodeRawValues.get(idx) ?? 0.5;
                    texData[idx * 4 + 0] = Math.round(finalNorm * 255);
                }
                texData[idx * 4 + 3] = 255;  // A = highlighted
                texHighlightedCount++;
            });
        });

        console.log(`🔍 [highlight] texture built: ${nodeCount} total texels, ${texHighlightedCount} highlighted (A=255)`);
        // Sanity check: read back the first highlighted texel
        const sampleHighlightIdx = highlightIndices[0];
        if (sampleHighlightIdx !== undefined) {
            console.log(`🔍 [highlight] texel[${sampleHighlightIdx}]: R=${texData[sampleHighlightIdx*4]}, G=${texData[sampleHighlightIdx*4+1]}, B=${texData[sampleHighlightIdx*4+2]}, A=${texData[sampleHighlightIdx*4+3]} (A should be 255)`);
        }

        const highlightTex = new Cesium.TextureUniform({
            typedArray:          texData,
            width:               nodeCount,
            height:              1,
            pixelFormat:         Cesium.PixelFormat.RGBA,
            pixelDatatype:       Cesium.PixelDatatype.UNSIGNED_BYTE,
            minificationFilter:  Cesium.TextureMinificationFilter.NEAREST,
            magnificationFilter: Cesium.TextureMagnificationFilter.NEAREST,
        });

        const prevShader = window.drone.model.customShader ?? null;

        // 🔍 expose a debug helper on window so you can type in the browser console:
        //   window._hlDebug(1)  → colours every fragment by normalised nodeIdx
        //                         (smooth gradient = featureId_0 works; solid colour = broken)
        //   window._hlDebug(0)  → back to normal highlight mode
        console.log('🔍 [highlight] TIP: run  window._hlDebug(1)  in the browser console to test TEXCOORD_1. A smooth colour gradient across the building = UVs work. A solid single colour = TEXCOORD_1 is not being read.');

        const customShader = new Cesium.CustomShader({
            translucencyMode: Cesium.CustomShaderTranslucencyMode.TRANSLUCENT,
            isTranslucent: true,
            uniforms: {
                u_highlightMap: { type: Cesium.UniformType.SAMPLER_2D, value: highlightTex },
                // NOTE: u_nodeCount removed — the U coordinate is pre-baked into TEXCOORD_1
                // so we no longer need to divide by nodeCount in the shader.
                u_pickMode:     { type: Cesium.UniformType.FLOAT,      value: 0.0 },
                u_debugMode:    { type: Cesium.UniformType.FLOAT,      value: 0.0 },
            },
            fragmentShaderText: `
// Heatmap: blue (low) → cyan → green → yellow → red (high)
vec3 heatmapColor(float t) {
    t = clamp(t, 0.0, 1.0);
    vec3 col;
    if (t < 0.25) {
        col = mix(vec3(0.0, 0.0, 1.0), vec3(0.0, 1.0, 1.0), t * 4.0);        // blue → cyan
    } else if (t < 0.5) {
        col = mix(vec3(0.0, 1.0, 1.0), vec3(0.0, 1.0, 0.0), (t - 0.25) * 4.0); // cyan → green
    } else if (t < 0.75) {
        col = mix(vec3(0.0, 1.0, 0.0), vec3(1.0, 1.0, 0.0), (t - 0.5)  * 4.0); // green → yellow
    } else {
        col = mix(vec3(1.0, 1.0, 0.0), vec3(1.0, 0.0, 0.0), (t - 0.75) * 4.0); // yellow → red
    }
    return col;
}

void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
    // TEXCOORD_1.x = pre-baked (nodeIdx + 0.5) / nodeCount, written by glbFlattener.
    // This replaces the EXT_mesh_features / featureId_0 approach which is unreliable
    // for entity-loaded GLBs in Cesium (only reliable for Cesium3DTileset).
    float u = fsInput.attributes.texCoord_1.x;
    vec4 texel = texture(u_highlightMap, vec2(u, 0.5));
    float isHighlighted = texel.a;      // A=255 → highlighted
    float normalisedVal = texel.r;      // R = value (0–1)

    // ── DEBUG: colour by U coordinate (smooth gradient = TEXCOORD_1 works) ──
    if (u_debugMode > 0.5) {
        material.diffuse = mix(vec3(0.0, 0.2, 1.0), vec3(1.0, 0.0, 0.2), u);
        material.alpha = 1.0;
        return;
    }

    if (isHighlighted > 0.5) {
        if (normalisedVal > 0.99) {
            material.diffuse = vec3(0.0, 0.749, 1.0);   // sky blue — asset query
        } else if (normalisedVal > 0.98) {
            material.diffuse = vec3(1.0, 0.133, 0.133); // red — compliance FAIL
        } else {
            material.diffuse = heatmapColor(normalisedVal); // gradient — KPI
        }
        material.alpha = 1.0;
    } else {
        if (u_pickMode > 0.5) { discard; }
        material.diffuse = vec3(0.45, 0.5, 0.6);
        material.alpha   = 0.3;
    }
}
`,
        });

        // Store DIRECT reference so we can mutate uniforms without going through
        // Cesium's Entity Property wrapper (which doesn't expose .uniforms).
        window._cesiumHighlightShader = customShader;
        window.drone.model.customShader = customShader;

        // ── Disable back-face culling ─────────────────────────────────────────
        // Walls/ducts in Revit GLBs are often single-sided shell meshes whose normals
        // point inward — from outside the building these faces are "back faces" and get
        // culled, so only the silhouette/boundary edge is visible.
        //
        // Strategy:
        //   1. Set ModelGraphics.backFaceCulling = false  (tells ModelVisualizer for
        //      future frames — works in Cesium 1.101+)
        //   2. Also find and set it on the actual Model primitive in scene.primitives
        //      directly, because ModelVisualizer re-creates the Model when customShader
        //      changes and may reset backFaceCulling to the original value during that
        //      rebuild cycle before our ModelGraphics value is re-read.
        window.drone.model.backFaceCulling = false;

        const applyBackFaceCullingOff = () => {
            // The entity's underlying Model primitive lives in scene.primitives and has
            // .id === the entity object.  Search for it and patch directly.
            const prims = window.viewer?.scene?.primitives;
            if (!prims) return;
            let found = false;
            for (let i = 0; i < prims.length; i++) {
                const p = prims.get(i);
                if (p && (p.id === window.drone || p.id?.id === window.drone?.id)) {
                    p.backFaceCulling = false;
                    console.log(`✅ [highlight] backFaceCulling=false set on scene.primitives[${i}] (Model primitive)`);
                    found = true;
                    break;
                }
            }
            if (!found) {
                console.warn('⚠️ [highlight] Could not find drone Model primitive in scene.primitives — backFaceCulling may not be applied yet (will retry)');
            }
            return found;
        };

        // Apply immediately (may already exist if model was loaded before highlight)
        if (!applyBackFaceCullingOff()) {
            // Model primitive not yet in scene (CustomShader rebuild still in progress).
            // Retry on successive frames until found, up to ~2 seconds.
            let attempts = 0;
            const retryId = setInterval(() => {
                attempts++;
                if (applyBackFaceCullingOff() || attempts > 20) {
                    clearInterval(retryId);
                }
            }, 100);
        }

        // 🔍 Debug toggle — type in browser console after a highlight fires:
        //   window._hlDebug(1)  → gradient colour mode (tests TEXCOORD_1)
        //   window._hlDebug(0)  → normal highlight mode
        window._hlDebug = (on) => {
            const s = window._cesiumHighlightShader;
            if (!s?.uniforms?.u_debugMode) { console.warn('No active highlight shader'); return; }
            s.uniforms.u_debugMode.value = on ? 1.0 : 0.0;
            window.viewer?.scene?.requestRender?.();
            console.log(`🔍 [highlight] debug mode ${on ? 'ON (gradient)' : 'OFF (normal)'}`);
        };

        console.log(`🔍 [highlight] CustomShader applied. nodeCount=${nodeCount}`);

        // ── Tooltip: show on hover over any part of the building ─────────────
        const tooltip = getOrCreateTooltip();
        const handler = new Cesium.ScreenSpaceEventHandler(window.viewer.scene.canvas);

        handler.setInputAction((movement) => {
            // Set u_pickMode=1 so ghost fragments discard themselves during scene.pick(),
            // allowing the pick ray to reach the highlighted element underneath.
            const shader = window._cesiumHighlightShader;
            if (shader?.uniforms?.u_pickMode) shader.uniforms.u_pickMode.value = 1.0;

            const picked = window.viewer.scene.pick(movement.endPosition);

            if (shader?.uniforms?.u_pickMode) shader.uniforms.u_pickMode.value = 0.0;

            if (!Cesium.defined(picked)) { tooltip.style.display = 'none'; return; }

            let data        = null;
            let elementName = '';

            // ── Path 1: node._node.name → raw glTF node name = BIM element ID ─
            // The GLB preserves each original node. picked.detail.node._node is the
            // raw glTF node object; its .name = the BIM element ID (e.g. "5112").
            const rawNodeName = picked?.detail?.node?._node?.name || '';
            if (rawNodeName && entityTooltipMap[rawNodeName]) {
                elementName = rawNodeName;
                data        = entityTooltipMap[rawNodeName];
            }

            // ── Path 2: node._id as glTF node index → glbNodeNames lookup ────
            if (!data) {
                const nodeId = picked?.detail?.node?._id;
                if (nodeId !== undefined && nodeId !== null && window.glbNodeNames) {
                    const nodeName = window.glbNodeNames[nodeId] || '';
                    if (nodeName && entityTooltipMap[nodeName]) {
                        elementName = nodeName;
                        data        = entityTooltipMap[nodeName];
                    }
                }
            }

            // ── Path 3: Cesium Entity id/name (non-CustomShader path) ─────────
            if (!data && picked?.id) {
                const eid  = String(picked.id.id   || '');
                const name = String(picked.id.name || '');
                const d    = entityTooltipMap[eid] || entityTooltipMap[name];
                if (d) { elementName = eid || name; data = d; }
            }

            if (!data) { tooltip.style.display = 'none'; return; }

            const html = formatTooltipHtml(data, elementName);
            if (!html) { tooltip.style.display = 'none'; return; }

            tooltip.innerHTML = html;
            const rect = window.viewer.scene.canvas.getBoundingClientRect();
            tooltip.style.left    = (rect.left + movement.endPosition.x + 18) + 'px';
            tooltip.style.top     = (rect.top  + movement.endPosition.y - 18) + 'px';
            tooltip.style.display = 'block';
        }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

        // ── Cleanup ───────────────────────────────────────────────────────────
        window._clearCesiumGlbHighlight = () => {
            if (window.drone?.model) {
                window.drone.model.customShader    = prevShader;
                window.drone.model.backFaceCulling = true;
            }
            // Also restore on the actual Model primitive
            const prims = window.viewer?.scene?.primitives;
            if (prims) {
                for (let i = 0; i < prims.length; i++) {
                    const p = prims.get(i);
                    if (p && (p.id === window.drone || p.id?.id === window.drone?.id)) {
                        p.backFaceCulling = true;
                        break;
                    }
                }
            }
            window._cesiumHighlightShader = null;
            if (!handler.isDestroyed()) handler.destroy();
            if (tooltip) tooltip.style.display = 'none';
            window._clearCesiumGlbHighlight = null;
        };
        return;
    }

    // ── 2. Tooltip ────────────────────────────────────────────────────────────
    const tooltip = getOrCreateTooltip();

    // ── 3. Hover handler ──────────────────────────────────────────────────────
    const handler = new Cesium.ScreenSpaceEventHandler(window.viewer.scene.canvas);

    handler.setInputAction((movement) => {
        const picked = window.viewer.scene.pick(movement.endPosition);
        if (!Cesium.defined(picked)) { tooltip.style.display = 'none'; return; }

        const pickedEntity = picked.id;
        if (!pickedEntity) { tooltip.style.display = 'none'; return; }

        const eid  = String(pickedEntity.id   || '');
        const name = String(pickedEntity.name || '');

        // Only show tooltip if the picked entity is in our list — no fallback.
        const data = entityTooltipMap[eid] || entityTooltipMap[name];

        if (data) {
            const html = formatTooltipHtml(data, eid || name);
            if (!html) { tooltip.style.display = 'none'; return; }
            tooltip.innerHTML = html;
            const rect = window.viewer.scene.canvas.getBoundingClientRect();
            tooltip.style.left    = (rect.left + movement.endPosition.x + 18) + 'px';
            tooltip.style.top     = (rect.top  + movement.endPosition.y - 18) + 'px';
            tooltip.style.display = 'block';
        } else {
            tooltip.style.display = 'none';
        }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    // ── 4. Cleanup ────────────────────────────────────────────────────────────
    window._clearCesiumGlbHighlight = () => {
        restoreAll(originalStates);
        if (!handler.isDestroyed()) handler.destroy();
        if (tooltip) tooltip.style.display = 'none';
        window._clearCesiumGlbHighlight = null;
    };
};

// ── Registration ──────────────────────────────────────────────────────────────

/**
 * Call once (e.g. inside the Scenes useEffect) to register the highlight
 * function on window so renderArchieResponse can reach it.
 */
export const registerCesiumGlbHighlight = () => {
    window.highlightCesiumGlbEntities = highlightCesiumGlbEntities;
};

/**
 * Call on component unmount to tear down any active highlight and remove
 * the tooltip element from the DOM.
 */
export const cleanupCesiumGlbHighlight = () => {
    if (typeof window._clearCesiumGlbHighlight === 'function') {
        window._clearCesiumGlbHighlight();
    }
    delete window.highlightCesiumGlbEntities;
    const tooltip = document.getElementById(TOOLTIP_ID);
    if (tooltip) tooltip.parentNode.removeChild(tooltip);
};
